import { subscriptionCost } from "constants/subscriptions";

import React, { useState, useEffect, useContext, createContext } from "react";

import { getBitcoinerChallenge, getCreditsRecharge, getUserData, getAnsweredQuizzes, getSubscriptionsDetails } from "API/calls";
import { RechargeState, RechargeType, SubscriptionType } from "types/enums";
import { IBitcoinerChallenge, ICreditRecharge, ISummarieSettings, IUserData, IAnsweredQuizzes, IPublicNickname, ISubscriptionsDetails } from "types/types";
import { isAtTierOrHigher } from "utils";

export type IUserDataContext = {
  refreshData?: () => void;
  badgesCount?: number;
  language?: string;
  subscriptionType?: SubscriptionType;
  creditsBalance?: number;
  creditsUnclaimed?: number;
  mfaEnabled?: boolean;
  aff: string;
  affBy: string | null;
  subscriptionPaused: boolean;
  subscriptionPausedWarning: boolean;
  subscriptionPausedError: boolean;
  loading: boolean;
  summariesSettings: ISummarieSettings;
  handleChangeSummarySetting?: (key: string, checked: boolean) => void;
  refreshCreditsRecharge?: () => void;
  creditsRechargeType?: RechargeType | null;
  showInUSD: boolean;
  toggleShowInUSD?: () => void;
  isMasked: boolean;
  toggleMaskedMode?: () => void;
  activeCashbackSats?: boolean;
  bitcoinerChallenge?: IBitcoinerChallenge;
  answeredQuizzes?: IAnsweredQuizzes;
  hasDiscordId?: boolean;
  subscriptionsDetails?: ISubscriptionsDetails;
  subscriptionPausedBeforePlusRemoval?: boolean;
  subscriptionTypeBeforePlusRemoval?: SubscriptionType | "PLUS";
  publicNickname?: IPublicNickname | null;
};

const defaultState: IUserDataContext = {
  aff: "",
  affBy: null,
  subscriptionPaused: false,
  subscriptionPausedWarning: false,
  subscriptionPausedError: false,
  loading: true,
  showInUSD: false,
  isMasked: false,
  summariesSettings: {
    cashbackSats: true,
    dcaSummaries: true,
    dcaSummariesIncludingDisabled: true,
    gridSummaries: true,
    gridSummariesIncludingDisabled: true,
  },
};

const UserDataContext = createContext<IUserDataContext>(defaultState);

export const UserDataProvider: React.FC<{}> = ({ children }) => {
  const [badgesCount, setBadgesCount] = useState<number | undefined>(defaultState.badgesCount);
  const [language, setLanguage] = useState<string | undefined>(defaultState.language);
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType | undefined>(defaultState.subscriptionType);
  const [creditsBalance, setCreditsBalance] = useState<number | undefined>(defaultState.creditsBalance);
  const [creditsUnclaimed, setCreditsUnclaimed] = useState<number | undefined>(defaultState.creditsUnclaimed);
  const [mfaEnabled, setMfaEnabled] = useState<boolean | undefined>(defaultState.mfaEnabled);
  const [aff, setAff] = useState<string>(defaultState.aff);
  const [affBy, setAffBy] = useState<string | null>(defaultState.affBy);
  const [subscriptionPaused, setSubscriptionPaused] = useState<boolean>(defaultState.subscriptionPaused);
  const [shouldSubscriptionPausedWarning, setShouldSubscriptionPausedWarning] = useState<boolean>();
  const [subscriptionPausedWarning, setSubscriptionPausedWarning] = useState<boolean>(
    defaultState.subscriptionPausedWarning
  );
  const [subscriptionPausedError, setSubscriptionPausedError] = useState<boolean>(defaultState.subscriptionPausedError);
  const [loading, setLoading] = useState(defaultState.loading);
  const [summariesSettings, setSummariesSettings] = useState<ISummarieSettings>({
    cashbackSats: localStorage.getItem("cashbackSats") === null,
    dcaSummaries: localStorage.getItem("dcaSummaries") === null,
    dcaSummariesIncludingDisabled: localStorage.getItem("dcaSummariesIncludingDisabled") === null,
    gridSummaries: localStorage.getItem("gridSummaries") === null,
    gridSummariesIncludingDisabled: localStorage.getItem("gridSummariesIncludingDisabled") === null,
  });
  const [creditsRechargeType, setCreditsRechargeType] = useState<RechargeType | null | undefined>(null);
  const [showInUSD, setShowInUSD] = useState<boolean>(localStorage.getItem("showInUSD") === "true");
  const [isMasked, setIsMasked] = useState<boolean>(localStorage.getItem("isMasked") === "true");
  const [activeCashbackSats, setActiveCashbackSats] = useState<boolean | undefined>();
  const [bitcoinerChallenge, setBitcoinerChallenge] = useState<IBitcoinerChallenge | undefined>();
  const [answeredQuizzes, setAnsweredQuizzes] = useState<IAnsweredQuizzes | undefined>();
  const [hasDiscordId, setHasDiscordId] = useState<boolean>();
  const [subscriptionsDetails, setSubscriptionsDetails] = useState<ISubscriptionsDetails | undefined>();
  const [subscriptionPausedBeforePlusRemoval, setSubscriptionPausedBeforePlusRemoval] = useState<boolean | undefined>();
  const [subscriptionTypeBeforePlusRemoval, setSubscriptionTypeBeforePlusRemoval] = useState<SubscriptionType | "PLUS" | undefined>();
  const [publicNickname, setPublicNickname] = useState<IPublicNickname | null>(null);

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (!loading && !!subscriptionType && shouldSubscriptionPausedWarning !== undefined) {
      loadCreditsRecharge();
    }
  }, [loading, subscriptionType, shouldSubscriptionPausedWarning]);

  async function loadUserData() {
    try {
      const userData = (await getUserData()).data as IUserData;
      if (userData) {
        setBadgesCount(userData.badgesCount);
        setLanguage(userData.language);
        setSubscriptionType(userData.subscriptionType);
        setCreditsBalance(userData.creditsBalance);
        setCreditsUnclaimed(userData.creditsUnclaimed);
        setMfaEnabled(userData.mfaEnabled);
        setAff(userData.aff);
        setAffBy(userData.affBy);
        setHasDiscordId(userData.hasDiscordId);
        const hasSubscriptionPausedError
          = userData.subscriptionPaused && userData.subscriptionType !== SubscriptionType["FREE"];
        const hasEnoughCreditsForNextMonth
          = !!userData.creditsBalance && userData.subscriptionType && userData.creditsBalance >= subscriptionCost[userData.subscriptionType];
        const hasSubscriptionPausedWarning
          = !hasSubscriptionPausedError && userData.subscriptionType !== SubscriptionType["FREE"] && !hasEnoughCreditsForNextMonth;
        const isPremium = isAtTierOrHigher(userData.subscriptionType, SubscriptionType.PREMIUM);
        setActiveCashbackSats(isPremium);
        setSubscriptionPaused(userData.subscriptionPaused);
        setShouldSubscriptionPausedWarning(hasSubscriptionPausedWarning);
        setSubscriptionPausedError(hasSubscriptionPausedError);
        loadBitcoinerChallenge();
        loadAnsweredQuizzes();
        setPublicNickname({ publicNickname: userData.publicNickname, publicNicknameState: userData.publicNicknameState });
        setSubscriptionPausedBeforePlusRemoval(userData.subscriptionPausedBeforePlusRemoval);
        setSubscriptionTypeBeforePlusRemoval(userData.subscriptionTypeBeforePlusRemoval);
        if (isPremium && !userData.subscriptionPaused) loadSubscriptionsDetails();
      } else {
        setSubscriptionType(undefined);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadAnsweredQuizzes() {
    try {
      const answeredQuizzesData = (await getAnsweredQuizzes()).data as IAnsweredQuizzes;
      if (answeredQuizzesData) setAnsweredQuizzes(answeredQuizzesData);
    } catch (error) {
      // Handle error if needed
    }
  }

  async function loadBitcoinerChallenge() {
    try {
      const bitcoinerChallengeData = (await getBitcoinerChallenge()).data as IBitcoinerChallenge;
      if (bitcoinerChallengeData) setBitcoinerChallenge(bitcoinerChallengeData);
    } catch (error) {
      // Handle error if needed
    }
  }

  async function loadCreditsRecharge() {
    try {
      const rechargeData = (await getCreditsRecharge()).data as ICreditRecharge;

      if (rechargeData && rechargeData.state === RechargeState.ACTIVE) {
        setCreditsRechargeType(rechargeData.type);
        setSubscriptionPausedWarning(false);
      } else {
        setSubscriptionPausedWarning(shouldSubscriptionPausedWarning ?? false);
        setCreditsRechargeType(undefined);
      }
    } catch (error) {
      //silent
    }
  }

  async function loadSubscriptionsDetails() {
    try {
      const subscriptionsDetailsData = (await getSubscriptionsDetails()).data as ISubscriptionsDetails;
      if (subscriptionsDetailsData) setSubscriptionsDetails(subscriptionsDetailsData);
    } catch (error) {
      // Handle error if needed
    }
  }

  const handleChangeSummarySetting = (key: string, checked: boolean) => {
    setSummariesSettings((prevState) => ({ ...prevState, [key]: checked }));

    if (checked) localStorage.removeItem(key);
    else localStorage.setItem(key, "false");
  };

  const toggleShowInUSD = () => {
    localStorage.setItem("showInUSD", (!showInUSD).toString());
    setShowInUSD((prevState) => !prevState);
  };

  const toggleMaskedMode = () => {
    localStorage.setItem("isMasked", (!isMasked).toString());
    setIsMasked((prevState) => !prevState);
  };

  return (
    <UserDataContext.Provider
      value={{
        badgesCount,
        language,
        subscriptionType,
        creditsBalance,
        creditsUnclaimed,
        mfaEnabled,
        aff,
        affBy,
        subscriptionPaused,
        subscriptionPausedWarning,
        subscriptionPausedError,
        refreshData: () => loadUserData(),
        loading,
        summariesSettings,
        handleChangeSummarySetting,
        refreshCreditsRecharge: () => loadCreditsRecharge(),
        creditsRechargeType,
        showInUSD,
        toggleShowInUSD,
        isMasked,
        toggleMaskedMode,
        activeCashbackSats,
        bitcoinerChallenge,
        answeredQuizzes,
        hasDiscordId,
        subscriptionsDetails,
        publicNickname,
        subscriptionPausedBeforePlusRemoval,
        subscriptionTypeBeforePlusRemoval,
      }}>
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserDataContext = () => useContext(UserDataContext);
